import {
    Box,
    capitalize,
    SelectChangeEvent,
    Theme,
    Typography
} from "@mui/material";
import { LoginLayout } from "../commonComponents";
import {
    GenerateTokenWithRoleResponseBody, GetInvestorJointHolderDetailsResponseBody, GetInvestorTokenCompanyResponseBody, GetRoleBasedPermissionsResponseBody, Roles, VerifyLoginOtpResponseBody
} from "../../redux-store/types/api-types";
import { useHistory } from "react-router";
import {
    Form,
    Formik,
    FormikProps
} from "formik";
import MFSelectField from "../../lib/formik/SelectField";
import * as yup from 'yup';
import { LoadingButton } from "@mui/lab";
import { useDispatch } from "react-redux";
import {
    generateTokenWithRole, getInvestorJointHolderDetails, getInvestorTokenCompany,
    getRoleBasedPermissions,
    showError
} from "../../redux-store/actions/auth";
import { useRef, useState } from "react";
import { VERIFY_LOGIN_OTP_SUCCESS } from "../../redux-store/types/auth";
import {
    storeApplicationId,
    storeInterfaceCode, storeInvestorClientId, storeIwappId
} from "../../redux-store/actions/id";
import { setInvestorRmId } from "../../redux-store/actions/sessionData";
import { USER_ROLES } from "../../utils/constant";
import { getCapitalizeRole } from "../../utils/utilityFunctions";

interface Values {
    role: Roles | null;
}

const initialValues: Values = {
    role: null
}

const validationSchema = yup.object().shape({
    role: yup
        .object()
        .nullable()
        .test(
            "role-required",
            "Role is required",
            function (value) {
                return !!value
            }
        )
})

export default function SelectRole(): JSX.Element {
    const history = useHistory()
    const dispatch = useDispatch()

    const routerState = history.location.state as VerifyLoginOtpResponseBody

    const [isFormSubmitting, setFormSubmitting] = useState<boolean>(false)

    const formikRef = useRef<FormikProps<Values>>(null);

    const handleSubmit = async (values: Values) => {
        try {
            setFormSubmitting(true)

            const response = await dispatch(generateTokenWithRole({
                role: values?.role?.roleId ?? "",
                temporaryToken: routerState?.token ?? ""
            }) as unknown as Promise<GenerateTokenWithRoleResponseBody>)

            if (!response) return;

            if (`${response?.role ?? ""}`.toLowerCase() === 'investor') {
                const investorJointHolderRes = await dispatch(getInvestorJointHolderDetails({
                    temporaryToken: response.token
                }) as unknown as Promise<GetInvestorJointHolderDetailsResponseBody>)

                if (investorJointHolderRes.isSuccess) {
                    if (investorJointHolderRes?.data?.accountList?.length === 1) {
                        dispatch(storeInvestorClientId({ investorClientId: investorJointHolderRes?.data?.accountList[0]?.clientCode }));
                        dispatch(storeInterfaceCode(investorJointHolderRes?.data?.accountList[0]?.clientInterfaceCode));
                        dispatch(storeIwappId({ iwappRmId: investorJointHolderRes?.data?.accountList[0]?.rmId ?? "" }));
                        dispatch(setInvestorRmId(investorJointHolderRes?.data?.accountList[0]?.rmId ?? ''))
                        const showDiyInvOnboardingPrev = (response?.role === USER_ROLES.INVESTOR) && ['sent_to_amc_approver', 'consent_sent_to_applicants'].includes(`${investorJointHolderRes?.data?.accountList?.[0]?.status ?? ''}`?.toLowerCase() as string);

                        const isKycDone = investorJointHolderRes?.data?.accountList?.[0]?.kycMethod !== 'investor';

                        const showDiyInvestorOnboarding = (response?.role === USER_ROLES.INVESTOR) && ['sent_to_investor'].includes(`${investorJointHolderRes?.data?.accountList?.[0]?.status ?? ''}`?.toLowerCase() as string);

                        const individualSelected = `${investorJointHolderRes?.data?.accountList?.[0]?.accountType ?? ''}`.toLowerCase() === 'individual account'
                        const nonIndividualSelected = `${investorJointHolderRes?.data?.accountList?.[0]?.accountType ?? ''}`.toLowerCase() === 'non-individual account'
                        const jointSelected = `${investorJointHolderRes?.data?.accountList?.[0]?.accountType ?? ''}`.toLowerCase() === 'joint account'

                        const tokenWithCompanyRes = await dispatch(getInvestorTokenCompany({
                            clientCode: investorJointHolderRes?.data?.accountList?.[0]?.clientCode,
                            applicationId: investorJointHolderRes?.data?.accountList?.[0]?.id,
                            temporaryToken: response.token
                        }) as unknown as Promise<GetInvestorTokenCompanyResponseBody>)

                        if (tokenWithCompanyRes.isSuccess) {
                            const permissionResponse = await dispatch(getRoleBasedPermissions({
                                temporaryToken: tokenWithCompanyRes?.token ?? ""
                            }) as unknown as Promise<GetRoleBasedPermissionsResponseBody>)
                            if (!permissionResponse) return;
                            dispatch(storeApplicationId({ applicationId: investorJointHolderRes?.data?.accountList?.[0]?.id }))
                            dispatch({
                                type: VERIFY_LOGIN_OTP_SUCCESS,
                                body: {
                                    ...tokenWithCompanyRes,
                                    diyInvestorOnboardingEdit: showDiyInvestorOnboarding, diyInvOnboardingPrev: showDiyInvOnboardingPrev,
                                    permissions: permissionResponse?.permissions[`${tokenWithCompanyRes?.role ?? ""}`.toLowerCase()] ?? permissionResponse?.permissions[`${tokenWithCompanyRes?.role ?? ""}`.toUpperCase()] ?? []
                                }
                            })


                            if (individualSelected && !isKycDone) {
                                history.replace(`/kyc-doc-selection`, { id: investorJointHolderRes?.data?.accountList?.[0]?.id });
                                return;
                            } else if (individualSelected && showDiyInvestorOnboarding) {
                                dispatch(showError('Please complete your Onboarding'));
                                history.replace(`/investor-onboarding-application/personal-details`, { id: investorJointHolderRes?.data?.accountList?.[0]?.id });
                                return;
                            } else if (((individualSelected || jointSelected) && showDiyInvOnboardingPrev)) {
                                history.replace(`/investor-onboarding-application-details/${investorJointHolderRes?.data?.accountList?.[0]?.id}`, { id: investorJointHolderRes?.data?.accountList?.[0]?.id });
                                return;
                            } else if (nonIndividualSelected && showDiyInvOnboardingPrev) {
                                history.replace(`/non-individual-application-preview/${investorJointHolderRes?.data?.accountList?.[0]?.id}`, { id: investorJointHolderRes?.data?.accountList?.[0]?.id });
                                return;
                            }
                        }

                    } else {
                        history.push('joint-holder', {
                            ...response,
                            jointHolderData: investorJointHolderRes.data.accountList
                        })
                    }
                }
            } else {
                const permissionsSelectionCriteria = values?.role?.subRoleId ? values?.role?.subRoleId : values?.role?.roleId;

                const permissionResponse = await dispatch(getRoleBasedPermissions({
                    temporaryToken: response?.token ?? ""
                }) as unknown as Promise<GetRoleBasedPermissionsResponseBody>)
                if (!permissionResponse) return;
                dispatch({
                    type: VERIFY_LOGIN_OTP_SUCCESS,
                    body: {
                        ...response,
                        permissions: permissionResponse?.permissions[`${permissionsSelectionCriteria ?? ""}`.toUpperCase()] ?? permissionResponse?.permissions[`${permissionsSelectionCriteria ?? ""}`.toLowerCase()] ?? permissionResponse?.permissions[permissionsSelectionCriteria ?? ''] ?? []
                    }
                });
            }
        } catch (error) {
            console.log("Error: ", error)
        } finally {
            setFormSubmitting(false)
        }
    }

    const handleRoleChange = ({ target: { value: selectedRoleId } }: SelectChangeEvent<unknown>) => {
        if (!formikRef?.current) {
            return
        }

        const { setFieldValue } = formikRef?.current ?? {}

        const selectedRoleObj = (routerState?.roles ?? []).find(obj => obj.roleId === selectedRoleId)

        setFieldValue("role", selectedRoleObj)
    }

    return (
        <LoginLayout
            LoginText=""
            renderChildUI
        >
            <Box
                sx={(theme: Theme) => ({
                    background: theme.palette.common.white,
                    width: "30%",
                    borderRadius: '24px',
                    padding: '24px',
                })}
            >
                <Box>
                    <Typography
                        variant="h2"
                        sx={(theme: Theme) => ({
                            color: theme.palette.primary[500]
                        })}
                    >
                        Select Role
                    </Typography>
                </Box>

                <Formik
                    innerRef={formikRef}
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                    validationSchema={validationSchema}
                >
                    {({ values }) => (
                        <Form>
                            < Box
                                sx={{
                                    marginTop: "20px",
                                    display: "flex",
                                    flexDirection: "column",
                                    rowGap: "15px"
                                }}
                            >
                                <MFSelectField
                                    name="role"
                                    label="Select Role *"
                                    value={values?.role?.roleId ?? ""}
                                    items={(routerState?.roles ?? []).map((obj) => ({
                                        ...obj,
                                        key: getCapitalizeRole(obj?.roleName ? obj.roleName : (obj?.roleId ?? "")),
                                        value: obj?.roleId ?? ""
                                    }))}
                                    onChange={handleRoleChange}
                                />
                                <LoadingButton
                                    type="submit"
                                    variant="contained"
                                    loading={isFormSubmitting}
                                    loadingPosition="center"
                                    sx={(theme: Theme) => ({
                                        ...theme.typography.body_500,
                                        fontWeight: 700,
                                        padding: "13px 56px",
                                        color: theme.palette.textDark.primary,
                                        borderRadius: "10px",
                                        backgroundColor: theme.palette.primary[500],
                                        [theme.breakpoints.down('sm')]: {
                                            padding: "10px 20px",
                                        },
                                        [theme.breakpoints.down('xs')]: {
                                            padding: "8px 16px",
                                        },
                                        alignSelf: "center"
                                    })}
                                >
                                    Submit
                                </LoadingButton>
                            </Box>
                        </Form>
                    )}
                </Formik>
            </Box>
        </LoginLayout >
    )
}